<div class="activity-page">
  <div class="activity-card">

    <header class="activity-header">
      <span class="eyebrow">Gestión de actividades</span>

      <h1>Crear nueva actividad</h1>

      <p>
        Registra una actividad y define sus fechas, tipo, valor en horas y
        capacidad de participantes.
      </p>
    </header>

    <form
      [formGroup]="activityForm"
      (ngSubmit)="onSubmit()"
      class="activity-form"
    >

      <!-- Información principal -->
      <section class="form-section">

        <div class="section-heading">
          <span class="section-number">01</span>

          <div>
            <h2>Información principal</h2>
            <p>Define los datos generales de la actividad.</p>
          </div>
        </div>

        <div class="form-grid">

          <div class="field field-full">
            <label for="title">Título</label>

            <input
              id="title"
              type="text"
              formControlName="title"
              class="form-control"
              placeholder="Ej. Jornada de adopción"
            />
          </div>

          <div class="field field-full">
            <label for="description">Descripción</label>

            <textarea
              id="description"
              formControlName="description"
              class="form-control description-field"
              placeholder="Describe brevemente la actividad..."
            ></textarea>
          </div>

          <div class="field">
            <label for="type">Tipo de actividad</label>

            <select
              id="type"
              formControlName="type"
              class="form-select"
            >
              <option value="" disabled>
                Selecciona un tipo
              </option>

              @for (type of activityTypes; track type) {
                <option [value]="type">
                  {{ type }}
                </option>
              }
            </select>
          </div>

        </div>

      </section>


      <!-- Fechas, horas y cupo -->
      <section class="form-section">

        <div class="section-heading">
          <span class="section-number">02</span>

          <div>
            <h2>Fechas, horas y cupo</h2>
            <p>
              Configura la fecha, el horario, el valor de la actividad
              y la cantidad máxima de participantes.
            </p>
          </div>
        </div>

        <div class="form-grid">

          <!-- FECHA DE INICIO -->
          <div class="field">
            <label for="startDate">
              Fecha de inicio
            </label>

            <input
              id="startDate"
              type="date"
              formControlName="startDate"
              class="form-control"
            />
          </div>


          <!-- FECHA LÍMITE -->
          <div class="field">
            <label for="dueDate">
              Fecha límite
            </label>

            <input
              id="dueDate"
              type="date"
              formControlName="dueDate"
              class="form-control"
            />
          </div>


          <!-- HORA DE INICIO -->
          <div class="field">
            <label for="startTime">
              Hora de inicio
            </label>

            <input
              id="startTime"
              type="time"
              formControlName="startTime"
              class="form-control"
            />
          </div>


          <!-- HORA DE FINALIZACIÓN -->
          <div class="field">
            <label for="endTime">
              Hora de finalización
            </label>

            <input
              id="endTime"
              type="time"
              formControlName="endTime"
              class="form-control"
            />
          </div>


          <!-- VALOR EN HORAS -->
          <div class="field">
            <label for="valueInHours">
              Valor en horas
            </label>

            <input
              id="valueInHours"
              type="number"
              formControlName="valueInHours"
              class="form-control"
              placeholder="0"
              min="0"
            />
          </div>


          <!-- CUPO DE PARTICIPANTES -->
          <div class="field field-full participant-limit-field">

            <label>
              Cupo de participantes
            </label>

            <p class="field-help">
              Decide si la actividad tendrá un número máximo de alumnos.
            </p>

            <div class="participant-limit-options">

              <!-- SIN LÍMITE -->
              <label
                class="participant-option"
                [class.selected]="!hasParticipantLimit"
              >
                <input
                  type="radio"
                  name="participantLimit"
                  [checked]="!hasParticipantLimit"
                  (change)="hasParticipantLimit = false; onParticipantLimitChange()"
                />

                <span class="participant-option-content">
                  <strong>Sin límite</strong>

                  <small>
                    Cualquier cantidad de alumnos puede inscribirse.
                  </small>
                </span>
              </label>


              <!-- CON LÍMITE -->
              <label
                class="participant-option"
                [class.selected]="hasParticipantLimit"
              >
                <input
                  type="radio"
                  name="participantLimit"
                  [checked]="hasParticipantLimit"
                  (change)="hasParticipantLimit = true; onParticipantLimitChange()"
                />

                <span class="participant-option-content">
                  <strong>Establecer límite</strong>

                  <small>
                    Define el número máximo de alumnos.
                  </small>
                </span>
              </label>

            </div>


            <!-- NÚMERO MÁXIMO -->
            @if (hasParticipantLimit) {

              <div class="participant-limit-input">

                <label for="maxParticipants">
                  Número máximo de participantes
                </label>

                <div class="participant-number-wrapper">

                  <input
                    id="maxParticipants"
                    type="number"
                    formControlName="maxParticipants"
                    class="form-control"
                    placeholder="Ej. 5"
                    min="1"
                    step="1"
                  />

                  <span>
                    participantes
                  </span>

                </div>


                <!-- ERROR -->
                @if (
                  activityForm.get('maxParticipants')?.invalid &&
                  activityForm.get('maxParticipants')?.touched
                ) {

                  <small class="field-error">
                    Ingresa un número entero mayor o igual a 1.
                  </small>

                }

              </div>

            }


            <!-- RESUMEN DEL CUPO -->
            <div class="participant-limit-summary">

              @if (hasParticipantLimit) {

                <span class="summary-icon">👥</span>

                <span>
                  Esta actividad tendrá un máximo de
                  <strong>
                    {{ activityForm.get('maxParticipants')?.value || 0 }}
                    participantes
                  </strong>.
                </span>

              } @else {

                <span class="summary-icon">∞</span>

                <span>
                  Esta actividad estará disponible
                  <strong>sin límite de participantes</strong>.
                </span>

              }

            </div>

          </div>

        </div>

      </section>


      <!-- Información adicional -->
      <section class="form-section final-section">

        <div class="section-heading">
          <span class="section-number">03</span>

          <div>
            <h2>Información adicional</h2>
            <p>
              Agrega comentarios o indicaciones para los participantes.
            </p>
          </div>
        </div>

        <div class="field">

          <label for="comments">
            Comentarios
          </label>

          <textarea
            id="comments"
            formControlName="comments"
            class="form-control comments-field"
            placeholder="Añade información adicional..."
          ></textarea>

        </div>

      </section>


      <!-- Acción final -->
      <div class="form-actions">

        <div>
          <strong>¿Actividad lista?</strong>

          <span>
            Revisa la información antes de crearla.
          </span>
        </div>

        <button
          type="submit"
          class="submit-button"
          [disabled]="!activityForm.valid || isCreating"
        >
          {{ isCreating ? 'Creando...' : 'Crear actividad' }}
        </button>

      </div>

    </form>

  </div>


  <!-- Mensaje de éxito -->
  @if (showSuccessMessage) {

    <div
      class="success-toast"
      role="status"
    >

      <div class="success-toast-icon">
        ✓
      </div>

      <div class="success-toast-content">

        <strong>
          Actividad creada
        </strong>

        <span>
          La actividad se creó correctamente.
        </span>

      </div>

    </div>

  }

</div>